import React, { useCallback, useEffect, useMemo } from 'react';
import cn from 'classnames';
import throttle from 'lodash/throttle';
import Header from './Header';
import Footer from './Footer';
import TableRow from './TableRow';
import { getTableRows } from '@/creditflowresearch/ui/components/league/Scorecard/StandingTable/helpers';
import { listenerDelay } from '@/creditflowresearch/ui/components/league/Scorecard/StandingTable/constants';
import { useScorecardContext } from '@/creditflowresearch/application/League/Scorecard/ScorecardContext';
import { Product } from '@/creditflowresearch/domain/Product';
import { AbsProduct } from '@/creditflowresearch/domain/Product';
import Region, { RegionType } from '@/creditflowresearch/domain/Region';

import standingStyles from '@/creditflowresearch/ui/components/league/Scorecard/StandingTable/standingTable.module.scss';
import styles from './volumeTable.module.scss';

const getColumnCountWithAmount = () => {
  switch (true) {
    case (document.body.offsetWidth < 615):
      return 1;
    case (document.body.offsetWidth < 670):
      return 2;
    case (document.body.offsetWidth < 830):
      return 1;
    case (document.body.offsetWidth < 990):
      return 2;
    case (document.body.offsetWidth < 1150):
      return 3;
    default:
      return 4;
  }
};

const getColumnCountWithoutAmount = () => {
  switch (true) {
    case (document.body.offsetWidth < 405):
      return 1;
    case (document.body.offsetWidth < 525):
      return 2;
    case (document.body.offsetWidth < 670):
      return 3;
    case (document.body.offsetWidth < 780):
      return 2;
    case (document.body.offsetWidth < 870):
      return 3;
    case (document.body.offsetWidth < 960):
      return 4;
    case (document.body.offsetWidth < 1050):
      return 5;
    case (document.body.offsetWidth < 1150):
      return 6;
    default:
      return 7;
  }
};

export interface IState {
  pageFilters: {
    productIds: string[],
    regionIds: string[],
  },
}

const VolumeTable = () => {
  const { activeProductIds, activeRegionIds } = useScorecardContext();
  const absOnly = Product.areAllOfType(activeProductIds, AbsProduct.TYPE);
  const usoaOnly = Region.areAllOfType(activeRegionIds, RegionType.USOA);
  const hasAmount = (absOnly && usoaOnly) || (activeProductIds.length === 0 && activeRegionIds.length === 0);
  const { state, setValuesTableColumnCount } = useScorecardContext();
  const { columnState, data: { categories = [], periods = [] } } = state;
  const { valueTableIndex: index, valueTableColumnCount: count } = columnState;
  const resizeHandler = useCallback(() => {
    const newCount = (hasAmount ? getColumnCountWithAmount : getColumnCountWithoutAmount)();

    if (count !== newCount) {
      setValuesTableColumnCount(newCount);
    }
  }, [setValuesTableColumnCount, count, hasAmount]);

  useEffect(() => {
    resizeHandler();
    const throttledResizeHandler = throttle(resizeHandler, listenerDelay);

    window.addEventListener('resize', throttledResizeHandler);
    return () => {
      window.removeEventListener('resize', throttledResizeHandler);
    };
  }, [resizeHandler]);

  const volumeTableRows = useMemo(() => getTableRows(categories), [categories]);
  const sliceOfPeriods = useMemo(() => periods.slice(index, count + index), [periods, index, count]);

  return (
    <div className={ cn(standingStyles.leftInnerShadow, styles.container) } data-test="scorecardTableVolume">
      <table className={ standingStyles.dataTable }>
        <Header
          periods={ sliceOfPeriods }
          hasAmount={ hasAmount }
        />
        <tbody>
          {
          volumeTableRows.map(({ key, ...row }, rowIndex) => (
            <TableRow
              key={ key }
              hasAmount={ hasAmount }
              rowIndex={ rowIndex }
              { ...row }
            />
          ))
        }
        </tbody>
        <Footer
          periods={ periods }
          hasAmount={ hasAmount }
        />
      </table>
    </div>
  );
};

export default VolumeTable;
